import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import {
    BaseComponent,
    ErrorsUtil,
    InputComponent,
    MessageBoxComponent,
    SelectComponent,
    WaitButtonComponent,
    WaitComponent
} from '@sircon/sircon-ui-framework';
import { UserHttpService } from 'src/providers/user-http.service';
import { UserContextService } from 'src/services/user-context.service';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ReferenceHttpService } from 'src/providers/reference-http.service';
import { catchError, finalize, of, takeUntil } from 'rxjs';
import { NgIf } from '@angular/common';
import {
    MfaManagementDialogComponent
} from 'src/components/account-settings/account-information/mfa-management-dialog/mfa-management-dialog.component';
import { FeatureToggles } from 'src/app/core/models/feature-toggles.model';

@Component({
    selector: 'app-account-security-questions',
    templateUrl: './account-security-questions.component.html',
    styleUrls: ['./account-security-questions.component.less'],
    imports: [NgIf, WaitComponent, MessageBoxComponent, FormsModule, ReactiveFormsModule, SelectComponent,
        InputComponent, WaitButtonComponent, MfaManagementDialogComponent]
})
export class AccountSecurityQuestionsComponent extends BaseComponent implements OnInit {
    @Output() back = new EventEmitter();
    @Output() questionsAnswered = new EventEmitter();
    @Input() workflow = false;
    @Input() accountSettings: 'reset' | 'add' | null = null;
    @Input() userId;
    doneLoading = false;
    loadingError = false;
    securityQuestions;
    securityQuestionsForQuestion1;
    securityQuestionsForQuestion2;
    securityQuestionsForQuestion3;

    errors: any = ErrorsUtil.create();
    submitting = false;
    formData;
    verified: WritableSignal<boolean> = signal(false);

    constructor(
        private userHttpService: UserHttpService,
        private userContextService: UserContextService,
        private formBuilder: FormBuilder,
        private referenceHttpService: ReferenceHttpService) {
        super();
        this.formData = this.formBuilder.group({
            question1: ['', Validators.required],
            answer1: ['', [Validators.required, Validators.maxLength(80), Validators.minLength(4)]],
            question2: ['', Validators.required],
            answer2: ['', [Validators.required, Validators.maxLength(80), Validators.minLength(4)]],
            question3: ['', Validators.required],
            answer3: ['', [Validators.required, Validators.maxLength(80), Validators.minLength(4)]]
        }, { validators: [] });
    }

    ngOnInit() {
        this.verified.set(!this.requiresVerification());
        this.loadData();
    }

    requiresVerification(): boolean {
        return this.accountSettings != null
            && this.userContextService.isFeatureToggleEnabled(FeatureToggles.ENABLE_MFA);
    }

    onPasswordVerified(): void {
        this.verified.set(true);
    }

    loadData() {
        this.referenceHttpService.securityQuestionsAsObservable()
            .pipe(
                catchError((response) => {
                    this.errors = ErrorsUtil.mapResponse(response);
                    this.loadingError = true;
                    return of();
                }),
                finalize(() => this.doneLoading = true),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe((response) => {
                this.securityQuestions = response.codes;
                this.securityQuestionsForQuestion1 = response.codes;
                this.securityQuestionsForQuestion2 = response.codes;
                this.securityQuestionsForQuestion3 = response.codes;
            });
    }

    saveQuestions() {
        ErrorsUtil.clear(this.errors);
        if (!this.formData.valid) {
            return;
        }

        const securityQuestionRequest = [
            { questionKey: this.formData.controls.question1.value, answer: this.formData.controls.answer1.value },
            { questionKey: this.formData.controls.question2.value, answer: this.formData.controls.answer2.value },
            { questionKey: this.formData.controls.question3.value, answer: this.formData.controls.answer3.value }
        ];
        this.submitting = true;
        this.userHttpService.saveSecurityQuestions(this.userId, securityQuestionRequest)
            .pipe(
                catchError((response) => {
                    this.errors = ErrorsUtil.mapResponse(response);
                    return of();
                }),
                finalize(() => this.submitting = false),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe(() => {
                this.questionsAnswered.emit();
            });
    }

    filterQuestions() {
        this.securityQuestionsForQuestion1 =
            this.securityQuestions.filter((question) =>
                question.value !== this.formData.controls.question2.value &&
                question.value !== this.formData.controls.question3.value);

        this.securityQuestionsForQuestion2 =
            this.securityQuestions.filter((question) =>
                question.value !== this.formData.controls.question1.value &&
                question.value !== this.formData.controls.question3.value);

        this.securityQuestionsForQuestion3 =
            this.securityQuestions.filter((question) =>
                question.value !== this.formData.controls.question1.value &&
                question.value !== this.formData.controls.question2.value);
    }
}
